import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import ClientAgreements from "../../components/ClientAgreements/ClientAgreements";
import Api from "../../services/Api";
import { getLanguage } from "../../lang/helpers";
import iconLearning from "url:shared-assets/icons/learning.svg";
import iconTask from "url:shared-assets/icons/task.svg";
import ChatThreadInput from "../AiThreads/components/ChatThreadInput";
import iconClock from "url:shared-assets/icons/clock.svg";
import iconAi from "url:shared-assets/icons/ai.svg";
import iconClose from "url:shared-assets/icons/close.svg";
import iconArrowRight from "url:shared-assets/icons/white/arrow-right.svg";
import iconInfoFilled from "url:shared-assets/icons/primary-500/info-filled.svg";
import CustomButton from "../../components/CustomButton";
import Loading from "../../components/Loading";
import UserFeedbackModal from "../../components/UserFeedbackModal";
import { storeAiStart } from "../../helpers/ai_thread";
import UsabilitySurvey from "../../components/UsabilitySurvey/UsabilitySurvey";
import OnboardingModal from "../../components/OnboardingModal/OnboardingModal";
import AttentionBox from "../../components/Boxes/AttentionBox";
import {LangMap, StrMap} from "../../interfaces/common";
import iconVisionStatement from "url:shared-assets/icons/visionStatement.svg";
import iconPlus2 from "url:shared-assets/icons/white/plus.svg";
import iconMinus from "url:shared-assets/icons/white/minus.svg";
import chevronRight from "url:shared-assets/icons/chevronRight.svg";
import iconChart from "url:shared-assets/icons/chart-Icon.svg";
import {attentionTxt} from "../../helpers/misc_texts";

interface NudgeInterface {
  prio: number;
  order: number;
  type: string;
  id: string;
  extraId: string;
  name: string;
  title: string;
  text: string;
  minutes: string;
}

interface AnnouncementInterface {
  id: string,
  text: StrMap;
  linkText: StrMap,
  url: string
}

interface OngoingActivityInterface {
  id: string;
  parentId?: string;
  fromChat?: boolean;
  type: string;
  title: string;
  deadline?: string;
  subtasks: number;
  subtasksDone: number
}

interface OnboardingData {
  hasCoach: boolean;
  hasAssessment: boolean;
}

enum ArrayName {
  ONGOING,
  RECENT_ACTIVITY,
}

interface ProgressActivity {
  id: string;
  parentId: string;
  type: string;
  name: string;
  date: string;
  deadline?: string;
}

interface ProgressInterface {
  doneToday: number;
  doneLastWeek: number;
  activities: Array<ProgressActivity>;
  next: Array<NextInterface>;
}

interface NextInterface {
  type: string;
  nudge: NudgeInterface;
}

interface OnboardingSteps {
  step: number;
  steps: number;
  nextStep?: string;
  linkId?: string;
}

interface OnboardingSummary {
  chatThreads: number;
  insightReports: number;
  lastChatTopic?: string;
  lastChatUpdated?: string;
  toolsCreated: number;
  visionStatements: number;
  sessions: number;
  activeWeeks: number;
  weekStreak: number;
  streakRecord: number;
  visionItems: number;
  visionProgress: number;
}

const introHeader: LangMap = {
  sv: {
    "self_assessment": "Kom igång med din utvecklingsresa!",
    "request_feedback": "Dags att be om feedback!",
    "read_report": "Din insiktsrapport är här",
    "new_request": "Skicka en ny feedback-förfrågan",
    "profile_first": "Utforska din personliga profil!",
    "profile_second": "Bara ett test kvar för din profil!",
    "vision_statement": "Skapa din första målbild"
  },
  en: {
    "self_assessment": "Start your growth journey!",
    "request_feedback": "Request Feedback",
    "read_report": "Your insights report is here!",
    "new_request": "Send a new feedback request",
    "profile_first": "Explore your Profile",
    "profile_second": "One more test to go",
    "vision_statement": "Create your first goal"
  }
}

const introText: LangMap = {
  sv: {
    "self_assessment": "Utveckling börjar med självinsikt. Börja med att genomföra din självskattning och be om feedback.",
    "request_feedback": "Be om feedback från personer runt dig för att få nya insikter.",
    "read_report": "Kolla in ditt resultat och få insikter om dina styrkor och utvecklingsområden.",
    "new_request": "Din förfrågan fick inte in tillräckligt med svar – försök igen och få de insikter du behöver.",
    "profile_first": "Få värdefulla insikter om vem du är och utveckla en djupare förståelse för dig själv.",
    "profile_second": "Slutför det sista testet i din profil för att få  fler och djupare insikter om dig själv.",
    "vision_statement": "Kolla in dina skräddarsydda utkast och skapa din målbild."
  },
  en: {
    "self_assessment": "Growth starts with self-awareness. Start by completing your self-assessment and request feedback",
    "request_feedback": "Time to request Feedback from those around you to gain insights.",
    "read_report": "Check out your results and get insights into your strengths and areas for development.",
    "new_request": "Your last one didn’t go through — try again and get the insights you need.",
    "profile_first": "Discover key insights about who you are and build a deeper understanding of yourself.",
    "profile_second": "Complete the last test in your Profile to gain more insights about yourself.",
    "vision_statement": "Explore your tailored drafts and create a goal."
  }
}

const Home: React.FC = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language);
  const [coachStyle, setCoachStyle] = useState<string>("coaching");
  const [loaded, setLoaded] = useState<boolean>(false);
  const [clientName, setClientName] = useState<string>("");
  const [nudges, setNudges] = useState<Array<NudgeInterface>>([]);
  const [emmaThinking, setEmmaThinking] = useState<boolean>(false);
  const [onboardingSteps, setOnboardingSteps] = useState<OnboardingSteps | null>(null);
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
  const [showSurveyType, setShowSurveyType] = useState<string|null>(null);
  const [clientAgreements, setClientAgreements] = useState<Array<any>>([]);
  const [ongoingActivities, setOngoingActivities] = useState<Array<OngoingActivityInterface>>([]);
  const [onboardingSummary, setOnboardingSummary] = useState<OnboardingSummary | null>(null);
  const [announcement, setAnnouncement] = useState<AnnouncementInterface | null>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  // Show more logic
  const SHOW_MORE_INCREMENT = 3;
  const [showMoreOnGoingCount, setShowMoreOnGoingCount] = useState(SHOW_MORE_INCREMENT);
  const [showMoreRecentActivityCount, setShowMoreRecentActivityCount] = useState(SHOW_MORE_INCREMENT);
  const setterShowMoreMap = {
    [ArrayName.ONGOING]: setShowMoreOnGoingCount,
    [ArrayName.RECENT_ACTIVITY]: setShowMoreRecentActivityCount,
  }
  const [progress, setProgress] = useState<ProgressInterface>({
    doneToday: 0,
    doneLastWeek: 0,
    activities: [],
    next: []
  });

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (loaded) {
      getData();
    }
  }, [lang]);

  const getData = () => {
    api
      .get("client/home", ["client_not_found"])
      .then((response) => {
        setClientName(response.json.clientName);
        setNudges(response.json.nudges);
        setClientAgreements(response.json.clientAgreements);
        setOnboardingData(response.json.onboardingData);
        setOnboardingSummary(response.json.onboardingSummary);
        setShowSurveyType(response.json.nextSurveyType);
        setOnboardingSteps(response.json.onboardingSteps);
        setOngoingActivities(response.json.ongoingActivities);
        setProgress(response.json.progress);
        setAnnouncement(response.json.announcement);
        setLoaded(true);
      })
      .catch((error) => {
        if (error.isExpectedError) {
          alert(error.json.message);
        } else {
          alert("Unknown error (http status " + error.response ? error.response.status : error.response + ")");
        }
      });
  };

  const toAiThread = (aiStyle: string, message: string | null) => {
    storeAiStart(aiStyle, "", null, null, null, message);
    navigate("/ai/0");
  };

  const startVoice = () => {
    storeAiStart(coachStyle, "voice", null, null, null, null);
    navigate("/ai/0");
  };

  const chatInputHeightChange = (height: number) => {
    if (chatWindowRef.current) {
      const winWidth = window.innerWidth;
      const ySpace = winWidth < 636 ? 138 : 154;
      const subtractHeight = ySpace + height;
      chatWindowRef.current.style.height =
        "calc(100dvh - " + subtractHeight + "px)";
      chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight);
    }
  };

  const clickNudge = (nudge: NudgeInterface) => {
    let url = "";
    if (nudge.type === "feedback_request") {
      url = "/development-plan";
    } else if (nudge.type === "self_assessment") {
      url = "/feedback/questionnaire/" + nudge.id;
    } else if (nudge.type === "action_finalize") {
      url = "/tasks/" + nudge.id;
    } else if (nudge.type === "action_coaching") {
      url = "/tasks/" + nudge.id;
    } else if (nudge.type === "focus_areas" || nudge.type === "goal") {
      url = "/development-plan";
    } else if (nudge.type === "vision_statement_feedback") {
      url = "/dev-plan/long-term-goal/" + nudge.id + "?tab=1";
    } else if (nudge.type === "dev-plan") {
      url = "/dev-plan";
    } else if (nudge.type === "assessments") {
      url = "/assessments";
    } else if (nudge.type === "profile") {
      url = "/profile";
    } else if (nudge.type === "ai") {
      url = "/ai/0";
    } else if (nudge.type === "survey_report") {
      url = "/dev-plan/survey/result/" + nudge.id + "/summary";
    }

    if (url.length > 0) {
      api
        .get("client/click?f=home_nudge:" + nudge.type + "&t=" + url)
        .then((response) => {
          navigate(url);
        });
    } else {
      alert("Missing link handler for " + nudge.type);
    }
  };

  const renderNudgeSmall = (_nudge: NudgeInterface, position: number) => {
    return (
      <div
        key={"nudge_" + position}
        className="bg-indigo-800 border-indigo-700 border-1 hover:bg-indigo-700 rounded-md p-3 cursor-pointer flex flex-col justify-between mt-2"
        onClick={() => clickNudge(_nudge)}
      >
        <div>
          <div className="flex items-center">
            {(_nudge.prio > 2 && _nudge.prio < 10) && (
              <img
                src={iconAi}
                className="h-4 w-4 rounded-full mr-1"
                alt="Emma"
              />
            )}
            <p
              className={
                "font-medium" + (position === 0 ? " mt-2 text-2xl" : "")
              }
            >
              {_nudge.name}
            </p>
          </div>

          <p className="mt-2 text-sm text-gray-400">{_nudge.text}</p>
        </div>

        <div className="flex justify-between items-end mt-4 gap-4">
          <div className="gap-2" style={{ width: "calc(100% - 32px)" }}>
            {(_nudge.title && _nudge.title.length > 0) && (
              <div className="flex items-center">
                <img
                  src={
                    _nudge.type.startsWith("action") ? iconTask : iconLearning
                  }
                  alt="learning"
                  className="h-4 w-4"
                />
                <div className="ml-2 z-title3 truncate">
                  {_nudge.title}
                </div>
              </div>
            )}

            {_nudge.minutes.length > 0 && (
              <div className="flex items-center">
                <img src={iconClock} alt="time" className="h-4 w-4" />
                <p className="ml-2 z-title3">{_nudge.minutes} Min</p>
              </div>
            )}
          </div>
          {position > 0 && (
            <img src={iconArrowRight} className="h-4 w-4" alt="Go" />
          )}
        </div>

        {position === 0 && (
          <CustomButton buttonType="blueBase" className="mt-4">
            {lang === "sv" ? "Fortsätt" : "Continue"}
          </CustomButton>
        )}
      </div>
    );
  };

  const clickActivity = (activity: ProgressActivity) => {
    if (activity.type === "vision_statement") {
      navigate('/dev-plan/long-term-goal/' + activity.id);
    } else if (activity.type === "sub-goal") {
      navigate('/dev-plan/long-term-goal/' + activity.parentId + "#goal_" + activity.id);
    } else if (activity.type === "activity") {
      navigate('/dev-plan/long-term-goal/' + activity.parentId + "#activity_" + activity.id);
    } else if (activity.type === "behavior") {
      navigate('/dev-plan/long-term-goal/' + activity.parentId + "#behavior_" + activity.id);
    } else if (activity.type === "task") {
      navigate('/tasks/' + activity.id);
    } else if (activity.type === "chat") {
      navigate('/ai/' + activity.id);
    } else {
      alert("No click handler for " + activity.type);
    }
  }

  const renderLastThing = (activity: ProgressActivity, nr: number) => {
    return (
      <div key={"last_" + activity.id} onClick={() => clickActivity(activity)}>
        <div className="mt-2 bg-indigo-900 hover:bg-indigo-800 p-4 rounded-md cursor-pointer">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1">
              <div className="text-gray-400 font-medium">
                {activity.date && <span className="z-body2">{activity.date.substring(0, 10)}: </span>}
                <span className="z-body2">{t(activity.type)}</span>
              </div>
              <div className="mt-2 z-title2">{activity.name}</div>
            </div>
            <img src={chevronRight} alt="chevroRight" className="h-6 w-6 shrink-0"/>
          </div>
        </div>
        <hr className="ml-[14px] border-indigo-700"/>
      </div>
    )
  }

  const handleShowMore = (isLimit: boolean, arrayName: ArrayName) => {
    setterShowMoreMap[arrayName](isLimit ? 3 : (prev) => prev + 3);
  }

  const renderOnboardingNextStep = () => {
    if (!onboardingSteps?.nextStep) {
      return;
    }

    const nextStepText = introText[lang][onboardingSteps.nextStep];
    let nextStepLink = "/dev-plan";

    let s = onboardingSteps?.nextStep;
    if (s === "self_assessment") {
      nextStepLink = "/assessments";
    } else if (s === "request_feedback") {
      nextStepLink = "/assessments";
    } else if (s.startsWith("profile")) {
      nextStepLink = "/profile";
    } else if (s === "wait_report") {
      nextStepLink = "/dev-plan";
    } else if (s === "read_report") {
      if (onboardingSteps?.linkId) {
        nextStepLink = "/dev-plan/survey/result/" + onboardingSteps?.linkId + "/summary";
      } else {
        nextStepLink = "/dev-plan";
      }
    } else if (s === "vision_statement") {
      nextStepLink = "/dev-plan";
    }

    return (
      <div className="mt-8 flex items-start justify-between">
        <div>
          <p className="z-title3">
            {lang === "sv" ? "Nästa steg" : "Next step"}:
          </p>
          <p className="z-body1">
            {nextStepText}
          </p>
        </div>

        <CustomButton buttonType="blueBase" style={{width: "unset"}} className="ml-4"
                      onClick={() => navigate(nextStepLink)}>
          {lang === "sv" ? "Fortsätt" : "Continue"}
        </CustomButton>
      </div>
    )
  }

  const clickOngoing = (ongoing: OngoingActivityInterface) => {
    if (ongoing.type === "task") {
      navigate('/tasks/' + ongoing.id);
    } else if (ongoing.type === "subGoal") {
      navigate('/dev-plan/long-term-goal/' + ongoing.parentId + "#goal_" + ongoing.id);
    } else if (ongoing.type === "activity") {
      navigate('/dev-plan/long-term-goal/' + ongoing.parentId + "#activity_" + ongoing.id);
    } else if (ongoing.type === "action") {
      navigate('/dev-plan/long-term-goal/' + ongoing.parentId + "#action_" + ongoing.id);
    } else if (ongoing.type === "behavior") {
      navigate('/dev-plan/long-term-goal/' + ongoing.parentId + "#behavior_" + ongoing.id);
    } else {
      alert("Unhandled: " + ongoing.type);
    }
  }

  const renderOnboardingSummary = () => {
    if (!onboardingSummary) {
      return null;
    }
    return (
      <div className="bg-indigo-900 border-indigo-800 border-1 p-5 rounded-md">
        <div className="mb-2 font-medium flex items-center gap-2 z-title3 pb-2 text-gray-400!">
          <img className= "h-4 w-4" src={iconChart} alt="toggle" />
          {lang === "sv" ? "Statistik" : "Stats overview"}
        </div>
        <div className="flex gap-x-4 pb-2">
          <div className="w-1/3 p-4 rounded-md bg-indigo-800 border-indigo-700 border-1">
            <p className="z-heading2 pb-2">{onboardingSummary.sessions}</p>
            <p className="z-body2">{lang === "sv" ? "Sessioner" : "Sessions"}</p>
          </div>
          <div className="w-1/3 p-4 rounded-md bg-indigo-800 border-indigo-700 border-1">
            <p className="z-heading2 pb-2">{onboardingSummary.activeWeeks}</p>
            <p className="z-body2">{lang === "sv" ? "aktiva veckor" : "Active weeks"}</p>
          </div>
          <div className="w-1/3 p-4 rounded-md bg-indigo-800 border-indigo-700 border-1">
            <div className="flex justify-between items-center">
              <p className="z-heading2 pb-2">{onboardingSummary.weekStreak}</p>
              <p className="z-body3 text-gray-400!">({lang === "sv" ? "Rekord" : "Record"}: {onboardingSummary.streakRecord})</p>
            </div>
            <p className="z-body2">{lang === "sv" ? "veckors streak" : "Week streak"}</p>
          </div>
        </div>
        <div className="flex mt-2 gap-x-4">
          <div className="w-1/3 p-4 rounded-md bg-indigo-800 border-indigo-700 border-1">
            <p className="z-heading2 pb-2">{onboardingSummary.chatThreads}</p>
            <p className="z-body2">{lang === "sv" ? "AI-konversationer" : "AI conversations"}</p>
          </div>
          <div className="w-1/3 p-4 rounded-md bg-indigo-800 border-indigo-700 border-1">
            <p className="z-heading2 pb-2">{onboardingSummary.visionStatements}</p>
            <p className="z-body2">{lang === "sv" ? "Målbilder skapade" : "Goals"}</p>
          </div>
          <div className="w-1/3 p-4 rounded-md bg-indigo-800 border-indigo-700 border-1">
            <p className="z-heading2 pb-2">{onboardingSummary.visionItems}</p>
            <p className="z-body2">{lang === "sv" ? "Målbilds-aktiviteter" : "Items in progress"}</p>
          </div>
        </div>
      </div>
    )
  }

  const renderOngoing = () => {
    return (
      <>
        {renderOnboardingSummary()}
      </>
    )
  }

  const clickAnnouncement = (action: number) => {
    if (announcement) {
      const url = announcement.url;
      api.get('client/announcement/' + announcement.id + '/' + action).then(response => {
        if (action === 1) {
          if (url.startsWith('https')) {
            document.location.href = url;
          } else {
            navigate(url);
          }
        } else {
          setAnnouncement(null);
        }
      });
    }
  }

  const renderAnnouncement = () => {
    if (!announcement) {
      return null
    }
    return (
      <div className="mt-4 border border-primary-500 bg-indigo-800 border-indigo-700 border-1 rounded-md p-4">
        <div className="flex justify-between items-center">
          <div className="flex">
            <img src={iconInfoFilled} alt="time" className="h-6 w-6"/>
            <div className="ml-2 z-title3">{announcement.text[lang]}</div>
            <div className="ml-2 z-title3 cursor-pointer underline hover:text-primary-500!" onClick={() => clickAnnouncement(1)}>
              {announcement.linkText[lang]}
            </div>
          </div>
          <div className="cursor-pointer hover:bg-indigo-700 rounded-[20px] p-1"
               onClick={() => clickAnnouncement(0)}>
            <img src={iconClose} alt="close" className="h-4 w-4"/>
          </div>
        </div>

      </div>
    )
  }

  if (!loaded) {
    return <Loading />;
  }

  if (clientAgreements.length > 0) {
    return (
      <ClientAgreements
        setAgreements={setClientAgreements}
        clientAgreements={clientAgreements}
        lang={lang}
      />
    );
  }

  const renderOnboardingSteps = () => {
    if (!onboardingSteps || !onboardingSteps.nextStep) {
      return (
        <>
          {
            nudges.map((nudge, i) => {
              return renderNudgeSmall(nudge, i);
            })
          }
        </>
      )
    }

    const intro = introHeader[lang][onboardingSteps.nextStep];
    let introText = lang === "sv" ? "Du är klar med onboardingen" : "You are done with the onboarding";
    if (onboardingSteps.step <= onboardingSteps.steps) {
      introText = "Onboarding";
      introText += lang === "sv" ? " steg " : " step ";
      introText += onboardingSteps.step;
      introText += lang === "sv" ? " av " : " of ";
      introText += onboardingSteps.steps;
    }
    let progress = 100 * onboardingSteps.step / onboardingSteps.steps;
    return (
      <div>
        <p className="z-heading1">{intro}</p>
        <p className="mt-8 z-body1">{introText}</p>
        <div className="mt-2 flex items-center">
          <div className="h-2 rounded-full bg-indigo-700" style={{width: "calc(100% - 40px)"}}>
            <div className="h-2 rounded-full bg-primary-500"
                 style={{width: progress + "%"}}></div>
          </div>
        </div>

        {renderOnboardingNextStep()}
      </div>

    )
  }

  const getActivityBg = (_ongoing: OngoingActivityInterface) => {
    const type = _ongoing.type;
    if (type === "activity") {
      if (_ongoing.fromChat) {
        return " bg-peach-600";
      } else {
        return " bg-orange-300";
      }
    }
    else if (type === "subGoal" || type === "action") return " bg-royal-700";
    else if (type === "behavior") return " bg-lavender-600";
    else if (type === "task") return " bg-peach-600";
    else return "";
  }

  const renderMain = () => {
    const todayDate = new Date();
    const isShowMoreOngoing = showMoreOnGoingCount >= ongoingActivities.length;
    const isShowMoreRecentActivityCount = showMoreRecentActivityCount >= progress.activities.length;
  
        return (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="bg-indigo-900 border-indigo-800 border p-5 rounded-md flex flex-col">
            <div className="z-title3 text-gray-400!">
              {lang === "sv" ? "Hej" : "Hi"} {clientName}
            </div>
            {renderOnboardingSteps()}
          </div>

          {/* DIV 2 */}
          {/* <div> */}
            {renderOngoing()}
          {/* </div> */}
          </div>
          

          {/* DIV 3*/}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 items-start">
          <div className="bg-indigo-900 border-indigo-800 border p-6 rounded-md">
            <div className="mb-2 font-medium flex items-center gap-2 z-title3 pb-2 text-gray-400!">
              <img className="h-4 w-4" src={iconVisionStatement} alt="toggle" />
              {lang === "sv" ? "Pågående" : "Ongoing"}
            </div>

            {ongoingActivities.length === 0 && (
              <div className="border border-dashed border-indigo-700 p-4 mb-2 rounded-md text-center">
                <img src={iconVisionStatement} alt="tip" className="mt-4 h-8 w-8 mx-auto" />
                <div className="mt-4 text-neutral-400">
                  {lang === "sv"
                    ? "Dina pågående aktiviteter visas här."
                    : "Your ongoing activities will appear here."}
                </div>
              </div>
            )}

            {ongoingActivities.slice(0, showMoreOnGoingCount).map((ongoing, i) => {
              let isDue = false;
              if (ongoing.deadline) {
                const deadline = new Date(ongoing.deadline);
                if (deadline < todayDate) {
                  isDue = true;
                }
              }
              return (
                <div
                  key={"ongoing_" + ongoing.id}
                  className="bg-indigo-800 border-indigo-700 border p-4 pl-7 mb-2 rounded-md hover:bg-indigo-700 cursor-pointer relative"
                  onClick={() => clickOngoing(ongoing)}
                >
                  <div className={"z-action-color" + getActivityBg(ongoing)}></div>
                  <div className="flex justify-between text-gray-400 font-medium">
                    <div className="z-title1">{ongoing.title}</div>
                    {ongoing.deadline && (
                      <div className="text-sm">
                        <span className="text-gray-100">{t("due-date")}:&nbsp;</span>
                        <span className="text-gray-400">{ongoing.deadline.substring(0, 10)}</span>
                      </div>
                    )}
                  </div>
                  {ongoing.type === "subGoal" && (
                    <div className="mt-2">
                      <span className="text-gray-100 font-medium">
                        {t("behaviors-and-actions")}:&nbsp;
                      </span>
                      <span className="text-gray-300">
                        {ongoing.subtasksDone} {t("of")}&nbsp;
                        {ongoing.subtasks} {t("completed").toLowerCase()}
                      </span>
                    </div>
                  )}
                  {isDue &&
                      <AttentionBox message={attentionTxt(lang, ongoing.type, !!ongoing.fromChat)} />
                  }
                </div>
              );
            })}

            {ongoingActivities.length > 3 && (
              <CustomButton
                buttonType="transparentBase"
                className="mt-4 mx-auto cursor-pointer"
                size="sm"
                onClick={() => handleShowMore(isShowMoreOngoing, ArrayName.ONGOING)}
                leftIcon={<img src={isShowMoreOngoing ? iconMinus : iconPlus2} alt="info" />}
                style={{ width: "unset" }}
              >
                {isShowMoreOngoing ? t("btn-show-less") : t("btn-show-more")}
              </CustomButton>
            )}
          </div>

          {/* DIV 4 */}
          {/* {progress.activities.length > 0 && ( */}
          <div className="bg-indigo-900 border-indigo-800 border-1 p-6 rounded-md">
            <div className="mb-2 font-medium flex items-center gap-2 z-title3 pb-2 text-gray-400!">
              <img className="h-4 w-4" src={iconClock} alt="toggle" />
              {lang === "sv" ? "Senaste aktiviteter" : "Recent activity"}
            </div>

            {progress.activities.length === 0 && (
                <div className="border border-dashed border-indigo-700 p-4 mb-2 rounded-md text-center">
                  <img src={iconClock} alt="tip" className="mt-4 h-8 w-8 mx-auto" />
                  <div className="mt-4 text-neutral-400">
                    {lang === "sv"
                      ? "Dina senaste aktiviteter visas här."
                      : "Your recent activities will appear here."}
                  </div>
                </div>
              )}

            {progress.activities.slice(0, showMoreRecentActivityCount).map((activity, i) => {
              return renderLastThing(activity, i);
            })}

            {progress.activities.length > 3 && (
              <CustomButton
                buttonType="transparentBase"
                className="mt-4 mx-auto cursor-pointer"
                size="sm"
                onClick={() => handleShowMore(isShowMoreRecentActivityCount, ArrayName.RECENT_ACTIVITY)}
                leftIcon={<img src={isShowMoreRecentActivityCount ? iconMinus : iconPlus2} alt="info"  className="h-4 w-4"/>}
                style={{ width: "unset" }}
              >
                {isShowMoreRecentActivityCount ? t("btn-show-less") : t("btn-show-more")}
              </CustomButton>
            )}
          </div>
          {/* )} */}
          </div>

        {/* Chat input — outside the grid so it doesn't become a 3rd grid item */}
        <div className="z-chat-bottom">
          <div className="z-chat-bottom-box bg-indigo-900">
            <ChatThreadInput
              sendToOtherPage={true}
              handleSendMessage={(msg) => toAiThread(coachStyle, msg)}
              onHeightChange={chatInputHeightChange}
              setCoachStyle={setCoachStyle}
              coachStyle={coachStyle}
              startVoice={startVoice}
              placeholder={
                lang === "sv"
                  ? "Skriv något för att starta ett eget ämne..."
                  : "Ask anything to start a custom topic..."
              }
              emmaThinking={emmaThinking}
            />
          </div>
        </div>

        {/* Insert space so that bottom search box doesn't cover bottom content */}
        <div className="h-32">&nbsp;</div>
      </>
    );
  };

  return (
    <div className="">
      {renderAnnouncement()}
      {renderMain()}
      {showSurveyType === "nps" && (
        <UserFeedbackModal
          setShowSurveyType={setShowSurveyType}
        />
      )}
      {showSurveyType === "usability" && (
        <UsabilitySurvey
          setShowSurveyType={setShowSurveyType}
        />
      )}

      {
        onboardingData &&
          <OnboardingModal close={() => setOnboardingData(null)} hasCoach={onboardingData.hasCoach}
                           hasAssessment={onboardingData.hasAssessment} />
      }
    </div>
  );
};

export default Home;
